import type { GeoMetricRow } from '../../types/geoAnalytics'
import { formatLean } from './choropleth'
import { BIVARIATE_PALETTE } from './viz/mapRamps'

export { BIVARIATE_PALETTE }

/** Spec-fixed thresholds (not user-editable in v1). Lean ≤ −5 → R, ≥ +5 → D. */
export const LEAN_BREAKS = { r: -5, d: 5 } as const
/** Turnout < 45 low, 45–60 mid, > 60 high. */
export const TURNOUT_BREAKS = { low: 45, high: 60 } as const

/** Strategy labels on the corners (+ Persuade on even/high per the design). Middle band = null. */
export const QUADRANT_LABELS: (string | null)[][] = [
  ['Skip', 'Watch', 'Mobilize'],     // low turnout
  [null, null, null],                // mid
  ['Opposition', 'Persuade', 'Defend'], // high
]

export interface QuadrantCell {
  row: 0 | 1 | 2
  col: 0 | 1 | 2
  color: string
  label: string | null
}

/** Human-readable lean/turnout bucket words, indexed the same way as the palette (col/row). */
const LEAN_WORD = ['R-leaning', 'even lean', 'D-leaning'] as const
const TURNOUT_WORD = ['low turnout', 'mid turnout', 'high turnout'] as const

/**
 * Short "why" phrase for a quadrant cell, e.g. "D-leaning + low turnout → Mobilize".
 * The mid-turnout band carries no strategy label by design (see QUADRANT_LABELS),
 * so it falls back to an explicit no-action phrase instead of an empty string —
 * this is what gives centrist/mid-turnout units a rationale in the popup + legend.
 */
export function quadrantRationale(row: 0 | 1 | 2, col: 0 | 1 | 2): string {
  const lean = LEAN_WORD[col]
  const turnout = TURNOUT_WORD[row]
  const label = QUADRANT_LABELS[row][col]
  return label
    ? `${lean} + ${turnout} → ${label}`
    : `${lean} + ${turnout} — no priority action`
}

export function classifyQuadrant(lean: number, turnout: number): QuadrantCell {
  const col = lean <= LEAN_BREAKS.r ? 0 : lean >= LEAN_BREAKS.d ? 2 : 1
  const row = turnout < TURNOUT_BREAKS.low ? 0 : turnout <= TURNOUT_BREAKS.high ? 1 : 2
  return {
    row: row as 0 | 1 | 2,
    col: col as 0 | 1 | 2,
    color: BIVARIATE_PALETTE[row][col],
    label: QUADRANT_LABELS[row][col],
  }
}

export function buildQuadrantGeoJson(
  units: GeoJSON.FeatureCollection,
  leanRows: GeoMetricRow[],
  turnoutRows: GeoMetricRow[],
): GeoJSON.FeatureCollection {
  const leanBy = new Map(leanRows.map(r => [r.geoUnitId, r]))
  const turnoutBy = new Map(turnoutRows.map(r => [r.geoUnitId, r]))
  return {
    type: 'FeatureCollection',
    features: units.features.map(feature => {
      const id = feature.properties?.id as string | undefined
      const lean = id ? leanBy.get(id) : undefined
      const turnout = id ? turnoutBy.get(id) : undefined
      const hasValue = lean?.value != null && turnout?.value != null
      const suppressed = !!lean?.suppressed || !!turnout?.suppressed

      let cellColor: string | null = null
      let quadrantLabel: string | null = null
      let displayValue = suppressed ? 'Insufficient data' : 'No data'
      if (hasValue) {
        const cell = classifyQuadrant(lean!.value!, turnout!.value!)
        cellColor = cell.color
        quadrantLabel = cell.label
        // Append the (lean bucket, turnout bucket) rationale so mid-turnout units —
        // which have no strategy label — still explain themselves in the popup.
        displayValue = `${formatLean(lean!.value!)} · turnout ${turnout!.value!.toFixed(1)}% — ${quadrantRationale(cell.row, cell.col)}`
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          hasValue,
          suppressed,
          cellColor,
          quadrantLabel,
          displayValue,
        },
      }
    }),
  }
}

/** Fill from the precomputed cellColor property; transparent when no value (hatch layer handles suppressed). */
export function quadrantFillExpression(): unknown[] {
  return [
    'case',
    ['!', ['to-boolean', ['get', 'hasValue']]],
    'rgba(0,0,0,0)',
    ['to-string', ['get', 'cellColor']],
  ]
}
